import React, { useEffect, useRef, useState } from "react";
import YouTube from "react-youtube";
import { useKaraoke } from "../../store/KaraokeStore";
import { Disc3, Radio } from "lucide-react";
import PresentationOverlay from "./PresentationOverlay";
import EndOfVideoOverlay from "./EndOfVideoOverlay";

// Static player options — module-scoped so the reference is stable and
// react-youtube does not reload the iframe on every re-render.
const YT_OPTS = {
  width: "100%",
  height: "100%",
  playerVars: {
    autoplay: 0,
    controls: 1,
    modestbranding: 1,
    rel: 0,
    fs: 1,
    playsinline: 1,
    iv_load_policy: 3,
  },
};

const PlayerPanel = () => {
  const { currentSong, isPlaying, advanceWithRating, setIsPlaying, volume, secondScreenOpen, presenting, skipPresentation, handleUnplayable, restartSignal } = useKaraoke();
  const playerRef = useRef(null);
  const [showEndOverlay, setShowEndOverlay] = useState(false);

  // Restart the current song from the start when the host presses "Reiniciar"
  useEffect(() => {
    if (!restartSignal || !playerRef.current) return;
    try {
      playerRef.current.seekTo(0, true);
      playerRef.current.playVideo();
    } catch (e) {
      console.error("Player restart failed", e);
    }
  }, [restartSignal]);

  // Reset overlays when song changes
  useEffect(() => {
    setShowEndOverlay(false);
  }, [currentSong?.videoId]);

  useEffect(() => {
    if (!playerRef.current) return;
    try {
      if (isPlaying) playerRef.current.playVideo();
      else playerRef.current.pauseVideo();
    } catch (e) {
      console.error("Player play/pause failed", e);
    }
  }, [isPlaying, currentSong?.videoId]);

  useEffect(() => {
    if (!playerRef.current) return;
    try {
      // When second screen is open, the audio is emitted there.
      // Main player must be muted to prevent double playback.
      if (secondScreenOpen) {
        playerRef.current.mute();
      } else {
        playerRef.current.unMute();
        playerRef.current.setVolume(volume);
      }
    } catch (e) {
      console.error("Player mute/volume update failed", e);
    }
  }, [volume, secondScreenOpen]);

  // Resume playback if browser pauses it when tab is hidden / window minimized
  useEffect(() => {
    const resume = () => {
      if (!playerRef.current) return;
      try {
        if (isPlaying) playerRef.current.playVideo();
      } catch (e) {
        console.error("Player resume on visibility failed", e);
      }
    };
    document.addEventListener("visibilitychange", resume);
    window.addEventListener("focus", resume);
    return () => {
      document.removeEventListener("visibilitychange", resume);
      window.removeEventListener("focus", resume);
    };
  }, [isPlaying]);

  // MediaSession: tells Chrome we're a music app → prevents background throttling
  useEffect(() => {
    if (!("mediaSession" in navigator) || !currentSong) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: currentSong.title || "Karaoke",
        artist: currentSong.singer || "Invitado",
        album: "DJ Gabo Karaoke",
        artwork: currentSong.thumbnail
          ? [{ src: currentSong.thumbnail, sizes: "480x360", type: "image/jpeg" }]
          : [],
      });
      navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";
    } catch (e) {
      console.error("MediaSession metadata update failed", e);
    }
  }, [currentSong, isPlaying]);

  // Silent Web Audio loop keeps the tab marked as "audible" (avoids being frozen)
  const audioCtxRef = useRef(null);
  useEffect(() => {
    if (!isPlaying) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0.0001;
        osc.frequency.value = 80;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        audioCtxRef.current = { ctx, osc };
      } else if (audioCtxRef.current.ctx.state === "suspended") {
        audioCtxRef.current.ctx.resume();
      }
    } catch (e) {
      console.error("Silent audio keep-alive failed", e);
    }
  }, [isPlaying]);

  const onReady = (e) => {
    playerRef.current = e.target;
    if (secondScreenOpen) {
      e.target.mute();
    } else {
      e.target.setVolume(volume);
    }
    if (isPlaying) e.target.playVideo();
  };

  const onEnd = () => {
    setShowEndOverlay(true);
    advanceWithRating();
  };

  const onError = (e) => {
    // 101 / 150 = embed disabled by owner (blocked/unplayable)
    const code = e?.data;
    if (code === 101 || code === 150) {
      // Do NOT auto-advance: invalidate this version + prompt the DJ to recover.
      handleUnplayable(`youtube-error-${code}`);
    }
  };

  const onStateChange = (e) => {
    // 1 = playing, 2 = paused
    if (e.data === 1) setIsPlaying(true);
    else if (e.data === 2) setIsPlaying(false);
  };

  return (
    <section
      className="col-span-1 lg:col-span-2 flex flex-col h-full border border-border bg-surface rounded-lg overflow-hidden relative"
      data-testid="player-panel"
    >
      <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <p className="text-[10px] font-mono tracking-[0.25em] uppercase text-muted-foreground">
            /// Reproductor
          </p>
          {isPlaying && (
            <div className="flex items-end gap-0.5 h-4 text-primary ml-2">
              <span className="eq-bar" />
              <span className="eq-bar" />
              <span className="eq-bar" />
              <span className="eq-bar" />
            </div>
          )}
        </div>
        {secondScreenOpen && (
          <div className="flex items-center gap-2 text-secondary text-xs font-mono">
            <Radio className="w-3.5 h-3.5 animate-pulse" />
            2ª PANTALLA · AUDIO PROYECTADO
          </div>
        )}
      </div>

      <div className="flex-1 bg-black relative">
        {currentSong ? (
          <div className="absolute inset-0">
            <YouTube
              videoId={currentSong.videoId}
              opts={YT_OPTS}
              onReady={onReady}
              onEnd={onEnd}
              onError={onError}
              onStateChange={onStateChange}
              className="w-full h-full"
              iframeClassName="w-full h-full"
            />
          </div>
        ) : (
          <EmptyPlayerState />
        )}
        {presenting && (
          <PresentationOverlay
            singer={presenting.singer || presenting}
            dedication={presenting.dedication}
            onSkip={skipPresentation}
            mode="inline"
          />
        )}
        {showEndOverlay && !presenting && <EndOfVideoOverlay mode="inline" />}
      </div>

      {currentSong && (
        <div className="p-4 border-t border-border bg-surface-elevated/40 shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-mono tracking-[0.2em] uppercase text-secondary mb-1">
                ▸ Ahora canta
              </p>
              <h3 className="font-display text-2xl font-bold truncate" data-testid="current-singer">
                {currentSong.singer || "Invitado"}
              </h3>
              <p className="text-sm text-muted-foreground truncate mt-0.5">{currentSong.title}</p>
            </div>
            <Disc3 className={`w-10 h-10 text-primary shrink-0 ${isPlaying ? "animate-spin" : "opacity-50"}`} style={{ animationDuration: "4s" }} />
          </div>
        </div>
      )}
    </section>
  );
};

const EmptyPlayerState = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-8 bg-gradient-to-b from-black via-[#0a0a0a] to-black">
    <div className="relative w-32 h-32 mb-6">
      <div className="absolute inset-0 rounded-full bg-primary/10 blur-3xl animate-pulse" />
      <div className="relative w-full h-full rounded-full border border-primary/30 flex items-center justify-center bg-black/40">
        <Disc3 className="w-14 h-14 text-primary/70" strokeWidth={1.2} />
      </div>
    </div>
    <h2 className="font-display text-3xl font-black uppercase tracking-tight mb-2">
      Listos para <span className="text-primary neon-pink">cantar</span>
    </h2>
    <p className="text-sm text-muted-foreground max-w-sm">
      Busca canciones, agrégalas a la cola y presiona reproducir para iniciar la fiesta.
    </p>
  </div>
);

export default PlayerPanel;
